/** Pure view models for the phone layout. */
import type { ApprovalRequest, ApprovalsState, Blocker, Budget, BudgetStatus, CostsState, Machine, Project, Session } from '@orgus/protocol';
import { formatMoney } from '@orgus/ui';
import type { StatusTone } from '@orgus/ui';
import { forecastStatus } from '../costs/model.ts';
import { sortRequests } from '../approvals/decision.ts';

export function isApprovalsState(v: unknown): v is ApprovalsState {
  return !!v && typeof v === 'object' && Array.isArray((v as ApprovalsState).pending) && Array.isArray((v as ApprovalsState).recent);
}

export function isCostsState(v: unknown): v is CostsState {
  return !!v && typeof v === 'object' && typeof (v as CostsState).today === 'number' && Array.isArray((v as CostsState).budgetStatus);
}

/** The one order every surface uses (approvals/decision.ts): soonest deadline, then highest risk, then longest wait. */
export function sortInbox(pending: ApprovalRequest[]): ApprovalRequest[] {
  return sortRequests(pending);
}

export interface InboxSplit<A, S, B = never> {
  approvals: A[];
  /** Requests that stopped a session (expired or denied, not yet handled). */
  blocked: B[];
  /** Waiting sessions that no pending or blocked request already stands for. */
  waiting: S[];
  /** What the Inbox tab badge shows: the three lists together. */
  total: number;
}

/**
 * The phone Inbox: pending approvals, blocked requests, then sessions waiting for input that no
 * request already covers. The tab badge counts exactly what the sections list, so the badge
 * and the page always agree.
 */
export function splitInbox<A extends Pick<ApprovalRequest, 'sessionId'>, S extends Pick<Session, 'id'>, B extends { approval: Pick<ApprovalRequest, 'sessionId'> } = never>(
  pending: readonly A[],
  waiting: readonly S[],
  blocked: readonly B[] = [],
): InboxSplit<A, S, B> {
  const covered = new Set([...pending.map((a) => a.sessionId), ...blocked.map((b) => b.approval.sessionId)]);
  const rest = waiting.filter((s) => !covered.has(s.id));
  return { approvals: [...pending], blocked: [...blocked], waiting: rest, total: pending.length + blocked.length + rest.length };
}

/** A swipe past this fraction of the card width decides; less snaps back. */
export const SWIPE_THRESHOLD = 0.35;

export function swipeDecision(dx: number, width: number): 'allow' | 'deny' | null {
  if (width <= 0) return null;
  if (dx >= width * SWIPE_THRESHOLD) return 'allow';
  if (dx <= -width * SWIPE_THRESHOLD) return 'deny';
  return null;
}

/** High-risk requests need a deliberate tap with a confirmation, never a swipe or a lock-screen button. */
export const swipeAllowed = (a: Pick<ApprovalRequest, 'risk' | 'kind'>) => a.risk !== 'high' && a.kind !== 'question';

export function timeLeft(expiresAt: number, now: number): { label: string; urgent: boolean; expired: boolean } {
  const ms = expiresAt - now;
  if (ms <= 0) return { label: 'Expired', urgent: true, expired: true };
  const min = Math.floor(ms / 60_000);
  if (min < 1) return { label: `${Math.max(1, Math.floor(ms / 1000))}s left`, urgent: true, expired: false };
  if (min < 60) return { label: `${min}m left`, urgent: min < 3, expired: false };
  return { label: `${Math.floor(min / 60)}h ${min % 60}m left`, urgent: false, expired: false };
}

/** A short one-line preview of what a tool call wants to do. */
export function inputPreview(a: Pick<ApprovalRequest, 'tool' | 'input'>): string | null {
  const i = a.input;
  if (i == null) return null;
  if (typeof i === 'string') return i;
  if (typeof i === 'object') {
    const o = i as Record<string, unknown>;
    for (const k of ['command', 'file_path', 'path', 'url', 'pattern']) if (typeof o[k] === 'string') return o[k] as string;
    try {
      return JSON.stringify(i);
    } catch {
      return null;
    }
  }
  return String(i);
}

export interface BudgetBar {
  id: string;
  label: string;
  period: string;
  spent: number;
  limit: number;
  /** 0..1+, spent over limit. */
  fraction: number;
  /** 0..1+, forecast over limit. */
  forecastFraction: number;
  tone: StatusTone;
  stateLabel: string;
}

const PERIOD: Record<Budget['period'], string> = { day: 'Today', month: 'This month', total: 'All time' };

export function budgetLabel(b: Budget | undefined, projects: Record<string, Project>): string {
  if (!b) return 'Budget';
  switch (b.scope.kind) {
    case 'org':
      return 'Everything';
    case 'project':
      return projects[b.scope.projectId]?.name ?? 'A project';
    case 'session':
      return 'One session';
    case 'mission':
      return 'One mission';
  }
}

export function budgetBars(costs: CostsState, projects: Record<string, Project>): BudgetBar[] {
  const byId = new Map(costs.budgets.map((b) => [b.id, b]));
  return costs.budgetStatus
    .map((s: BudgetStatus): BudgetBar => {
      const b = byId.get(s.budgetId);
      const fraction = s.limit > 0 ? s.spent / s.limit : 0;
      const fs = forecastStatus(s);
      return {
        id: s.budgetId,
        label: budgetLabel(b, projects),
        period: b ? PERIOD[b.period] : '',
        spent: s.spent,
        limit: s.limit,
        fraction,
        forecastFraction: s.limit > 0 ? s.forecast / s.limit : 0,
        // The one forecast state the Spend page uses: a forecast over the limit is never On track.
        tone: fs.tone,
        stateLabel: fs.label,
      };
    })
    .sort((a, b) => b.fraction - a.fraction);
}

/** US dollars through the shared formatter: "$0.42", "$1,284", "$19.1K". */
export const money = (usd: number): string => formatMoney(usd);

/** Sessions burning tokens fastest, for the runaway list. */
export function topSpenders(costs: CostsState, n = 5) {
  return [...costs.sessions].sort((a, b) => b.total.cost - a.total.cost).slice(0, n);
}

/** How many finished missions the phone lists. */
export const FINISHED_ON_PHONE = 10;

/**
 * Missions for the phone, in three groups: waiting on a person first, then running or about
 * to start, then the most recently finished. Newest change first within each group.
 */
export function missionGroups<R extends { mission: { updatedAt: number }; view: { state: string } }>(rows: readonly R[]): { needsYou: R[]; running: R[]; finished: R[] } {
  const newest = [...rows].sort((a, b) => b.mission.updatedAt - a.mission.updatedAt);
  return {
    needsYou: newest.filter((r) => r.view.state === 'waiting'),
    running: newest.filter((r) => r.view.state === 'running' || r.view.state === 'planned'),
    finished: newest.filter((r) => r.view.state === 'done' || r.view.state === 'failed' || r.view.state === 'stopped').slice(0, FINISHED_ON_PHONE),
  };
}

export interface FleetMachine {
  machine: Machine;
  sessions: Session[];
}

/**
 * Phone lists of sessions: waiting ones on top, then newest started first. Never by last
 * activity, which changes on every update: rows reshuffled under a finger and a tap opened
 * another session. A row moves only when it starts or stops waiting for you.
 */
export function phoneSessionOrder(a: Pick<Session, 'id' | 'status' | 'startedAt'>, b: Pick<Session, 'id' | 'status' | 'startedAt'>): number {
  return Number(b.status === 'waiting') - Number(a.status === 'waiting') || b.startedAt - a.startedAt || a.id.localeCompare(b.id);
}

/** Machines (online first) with their live sessions, waiting ones on top (phoneSessionOrder). */
export function fleet(machines: Machine[], sessions: Session[]): FleetMachine[] {
  return [...machines]
    .sort((a, b) => Number(b.online) - Number(a.online) || a.name.localeCompare(b.name))
    .map((machine) => ({
      machine,
      sessions: sessions.filter((s) => s.machineId === machine.id && s.status !== 'ended').sort(phoneSessionOrder),
    }));
}

/**
 * The blockers a phone mission page lists under its state. A pending approval has its own
 * Answer button above, so it is left out; a decided one (expired or denied) that stopped the
 * session is the reason the mission is blocked, so it comes first (R-HL-3).
 */
export function phoneBlockers(blockers: readonly Blocker[], max = 2): Blocker[] {
  const decided = (b: Blocker) => b.kind === 'approval' && !!b.outcome && b.phase !== 'resuming';
  return blockers
    .filter((b) => b.kind !== 'approval' || decided(b))
    .sort((a, b) => Number(decided(b)) - Number(decided(a)))
    .slice(0, max);
}

/** Sessions flagged runaway: exactly the costs module's active runaways, the list the Spend page flags. */
export function activeRunaways(costs: Pick<CostsState, 'runaways'>): Set<string> {
  return new Set(costs.runaways.filter((r) => r.active).map((r) => r.sessionId));
}

/**
 * A page of a session's transcript for the phone (ORGU-185): the newest `pages * size` events,
 * newest first, and how many earlier ones Show earlier would add.
 */
export function transcriptPage<E extends { seq: number }>(events: readonly E[], pages: number, size: number): { shown: E[]; earlier: number } {
  const newest = [...events].sort((a, b) => b.seq - a.seq);
  const n = Math.max(1, pages) * size;
  return { shown: newest.slice(0, n), earlier: Math.max(0, newest.length - n) };
}
